import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Grid } from '@material-ui/core';
import { NEXT } from '../../../hooks/useStepNavigations';
import { getStandardSatellites } from '../../../common/satellites';
import { Actions as Mepbay } from '../../../store/ducks/mepbay';
import ReactGA from 'react-ga';
import { useTranslation } from 'react-i18next';
import TourGuider from '../tour/TourGuider';
import { useLocalStorage } from '../../../common/utils';
import HelpButton from '../core/HelpButton';
import { Button } from '@material-ui/core';
import { push } from 'connected-react-router';
import SatelliteCard from '../acquisition/SatelliteCard';
import { SATELLITE_GRID_MAX_WIDTH } from '../acquisition/SatelliteChooser';
import SatelliteNotice from '../acquisition/SatelliteNotice';

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles for the
 * MEPBay satellite chooser step.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Mepbay
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  content: {
    '&:not(:first-child)': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
  },
  /**
   * Same treatment as the shared chooser's row: MEPBay's card is a fraction of the viewport rather
   * than a fixed width, so without the cap its two satellite cards came out much larger than the
   * same two cards in every other module.
   * @see {@link SATELLITE_GRID_MAX_WIDTH}
   */
  grid: {
    margin: '0 auto',
    width: '100%',
    maxWidth: SATELLITE_GRID_MAX_WIDTH,
    padding: theme.spacing(2),
  },
  button: {
    marginBottom: theme.spacing(4),
  },
}));

/**
 * @function SatelliteChooser
 * @description First step of the MEPBay acquisition wizard. Lets the user pick which satellite
 * to use for the analysis.
 * @param {Object} props - Component properties.
 * @param {Function} props.navigate - Step navigation handler.
 * @see {@link handleChoice} - commits the selected satellite and advances.
 * **Called by:
 * @see {MepBayPage} - as the first acquisition step.
 * @memberof module:Mepbay
 * @returns {JSX.Element} The satellite selection UI.
 */
const SatelliteChooser = ({ navigate }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [t] = useTranslation();

  const steps = [
    {
      selector: '#satellitechooser',
      content: t('tour.acquisition.1.info'),
    },
    {
      selector: '#Landsat_props',
      content: t('tour.acquisition.1.Landsat.info'),
    },
    {
      selector: '#Sentinel-2_props',
      content: t('tour.acquisition.1.Sentinel-2.info'),
    },
  ];

  const [isTourOpen, setIsTourOpen] = useLocalStorage('showSatelliteTour', true);

  /**
   * @function handleChoice
   * @description Logs the satellite choice to Google Analytics, dispatches the selected
   * satellite index, and advances to the next step.
   * @param {Number} index - Index of the chosen satellite.
   * @param {String} type - Product type ("TOA" by default; forced to "SR" when index is 1 and the URL contains "csqueeze").
   * **Called by:
   * @see {SatelliteChooser} - via each SatelliteCard's onChoose prop.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const handleChoice = (index, type) => {
    ReactGA.event({
      category: 'Mepbay',
      action: 'ChooseSatellite',
      value: index,
    });
    dispatch(Mepbay.setSatellite(index));
    if (index == 1 && url.indexOf('csqueeze') !== -1) type = 'SR';
    navigate(NEXT);
  };

  let url = window.location.href;
  const satellites = getStandardSatellites();
  satellites.forEach((el) => (el.enabled = true));

  return (
    <Box>
      <HelpButton
        onClickFunction={() => {
          setIsTourOpen(true);
        }}
      />
      <Grid container spacing={3} justifyContent="center" id="satellitechooser" className={classes.grid}>
        {satellites.map((satellite, i) => (
          <Grid item key={i} xs={6} id={satellite.name}>
            <SatelliteCard
              name={satellite.name}
              provider={satellite.provider}
              image={satellite.image}
              cycle={satellite.summary.cycle}
              startYear={satellite.summary.startYear}
              endYear={satellite.summary.endYear}
              resolution={satellite.summary.opticalResolution}
              onChoose={(type = 'TOA') => handleChoice(i, type)}
              enabled={satellite.enabled}
              variant="modern"
              description={t(`forms.acquisition.1.card.about.${satellite.name}`, { defaultValue: '' })}
            />
          </Grid>
        ))}
        <TourGuider steps={steps} isOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
      </Grid>
      <SatelliteNotice />
      <Box textAlign="center">
        <Button
          variant="contained"
          onClick={() => {
            dispatch(push('/main/selection'));
          }}
          className={classes.button}>
          {t('forms.acquisition.prev')}
        </Button>
      </Box>
    </Box>
  );
};

export default SatelliteChooser;
